import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuthState } from '@/contexts/AuthContext';
import { EditModeContext, type EditModeContextType, type EditTarget } from '@/contexts/EditModeContext';

export function EditModeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user } = useAuthState();
  const canEdit = user !== null && (user.role === 'admin' || user.role === 'root_admin');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [isGuestView, setIsGuestView] = useState(false);

  useEffect((): void => {
    try {
      const stored = localStorage.getItem('ui_text_edit_mode');
      if (stored !== null) setIsEditMode(stored === '1');
      const storedGuestView = localStorage.getItem('ui_guest_view_mode');
      if (storedGuestView !== null) setIsGuestView(storedGuestView === '1');
    } catch (error: unknown) {
      console.error('Failed to restore inline-edit preferences', error);
    }
  }, []);

  useEffect((): void => {
    if (!canEdit && isEditMode) {
      setIsEditMode(false);
      setEditTarget(null);
    }
  }, [canEdit, isEditMode]);

  const handleSetEditMode = useCallback(
    (enabled: boolean): void => {
      if (!canEdit) return;
      setIsEditMode(enabled);
      if (!enabled) setEditTarget(null);
      try {
        localStorage.setItem('ui_text_edit_mode', enabled ? '1' : '0');
      } catch (error: unknown) {
        console.error('Failed to persist inline-edit mode', error);
      }
    },
    [canEdit]
  );

  const handleSetGuestView = useCallback(
    (enabled: boolean): void => {
      if (!canEdit) return;
      setIsGuestView(enabled);
      try {
        localStorage.setItem('ui_guest_view_mode', enabled ? '1' : '0');
      } catch (error: unknown) {
        console.error('Failed to persist guest preview mode', error);
      }
    },
    [canEdit]
  );

  const value = useMemo<EditModeContextType>(
    () => ({
      isEditMode,
      setEditMode: handleSetEditMode,
      canEdit,
      editTarget,
      setEditTarget,
      isGuestView,
      setGuestView: handleSetGuestView,
    }),
    [isEditMode, handleSetEditMode, canEdit, editTarget, isGuestView, handleSetGuestView]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}
